import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Users, MessageCircle, ArrowRight } from "lucide-react";

const TITLE = "CRM integrado para WhatsApp e Instagram com funil de leads | FabChat";
const DESCRIPTION =
  "CRM integrado para WhatsApp e Instagram com funil de leads, histórico de conversas e acompanhamento comercial no mesmo painel da FabChat.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "CRM integrado", path: "/integracoes/crm" },
];

export default function IntegracoesCrm() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/integracoes/crm" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">CRM integrado ao atendimento</h1>
          <p className="text-lg text-muted-foreground mb-12">
            <strong className="text-foreground">CRM nativo da FabChat</strong>: gerencie leads, funil e histórico de conversas do WhatsApp e Instagram em um único lugar, sem precisar conectar um CRM externo.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que é o CRM da FabChat</h2>
            <p className="text-muted-foreground mb-4">
              A FabChat oferece um <strong className="text-foreground">CRM nativo da plataforma</strong> — ou seja, não é integração com Salesforce, HubSpot ou outro sistema: o CRM já vem com a FabChat. As conversas do <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">WhatsApp</Link> e do <Link to="/instagram/agente-ia" className="text-primary hover:underline">Instagram</Link> viram leads organizados por estágio (funil). Você vê histórico de mensagens, anotações, estágio atual e pode mover contatos entre etapas. Tudo no mesmo painel em que a equipe atende e em que o agente de IA está configurado.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos do CRM para atendimento e vendas</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <Users className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Leads criados a partir das conversas (WhatsApp, Instagram), com nome, contato e estágio.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Funil configurável: organize estágios (ex.: novo lead, qualificado, negociação, fechado) e arraste os leads entre colunas.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Histórico de conversas e anotações por lead, visível no CRM e no chat.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Quem se beneficia</h2>
            <p className="text-muted-foreground mb-4">
              Times de vendas ou atendimento que já usam WhatsApp e Instagram e querem centralizar contatos e conversas sem depender de planilhas ou de um CRM separado. O CRM nativo da FabChat acompanha a jornada do contato desde o primeiro atendimento (humano ou pela IA) até o fechamento, com métricas no dashboard.
            </p>
          </section>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
